import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'
import { AuthMiddleware } from '../components/AuthMiddleware'
import {
  ADDRESS_SCHEDULER_APPLET_SCOPES,
  ALARM_SMS_REVIEW_APPLET_SCOPES,
  ALL_LOCATION_MANAGEMENT_SCOPES,
  ERT_SCOPE,
  MONITORING_MODERATION_APPLET_SCOPES,
  READ_ALARMS_SCOPE,
  READ_ALARM_SMS_INCIDENTS_SCOPE,
  READ_LOCATIONS_SCOPE
} from '../constants'
import { AppLayout } from '../layouts/AppLayout'
import { addressSchedulerRoutes } from './address-scheduler/addressScheduler.routes'
import { AlarmSmsPage } from './AlarmSmsPage'
import { AlarmsPage } from './AlarmsPage'
import { LandingPage } from './LandingPage'
import { NotFoundPage } from './NotFoundPage'
import { UnauthorizedPage } from './UnauthorizedPage'
import { redirectRoutes } from './redirectRoutes'
import { engagementReviewRoutes } from './engagement-review/engagementReview.routes'
import { monitoringModerationRoutes } from './monitoring-moderation/monitoringModeration.routes'
import { locationManagementRoutes } from './location-management/locationManagement.routes'
import { AppRoute, registerRoutes, toRouteObjects } from '../auth/routeAuth'

/**
 * Note: Top-level components need to render a Page Component
 */
const appRoutes: AppRoute[] = [
  ...redirectRoutes,
  {
    element: <AppLayout />,
    hydrateFallbackElement: <div>Initializing App...</div>,
    children: [
      {
        /*
         * This route is necessary to keep the query params used during the auth process.
         * A traditional redirect would drop them and trigger an infinite redirect loop.
         */
        index: true,
        element: <Navigate to={'/locations'} replace={true} />
      },
      {
        /*
         * AuthMiddleware reads the `handle.auth` metadata from all matched routes
         * and redirects to /unauthorized if any scope check fails.
         */
        element: <AuthMiddleware />,
        children: [
          {
            path: '/locations/:sid?',

            children: [
              {
                index: true,
                element: <LandingPage />
              },
              {
                auth: { scopes: ALARM_SMS_REVIEW_APPLET_SCOPES, mode: 'any' },
                element: <Outlet />,
                children: [
                  {
                    path: 'alarm-sms-review/',
                    element: <AlarmsPage />,
                    auth: { scopes: [READ_LOCATIONS_SCOPE, READ_ALARMS_SCOPE], mode: 'all' }
                  },
                  {
                    // TODO: Does it make sense to allow this page to load without a valid locationId?
                    path: 'alarm-sms-review/:alarmId?',
                    element: <AlarmSmsPage />,
                    auth: { scopes: [READ_LOCATIONS_SCOPE, READ_ALARMS_SCOPE, READ_ALARM_SMS_INCIDENTS_SCOPE], mode: 'all' }
                  }
                ]
              },
              {
                path: 'address-scheduler',
                auth: { scopes: ADDRESS_SCHEDULER_APPLET_SCOPES, mode: 'any' },
                element: <Outlet />,
                children: addressSchedulerRoutes
              },
              {
                path: 'engagement-review',
                auth: { scopes: [ERT_SCOPE], mode: 'any' },
                element: <Outlet />,
                children: engagementReviewRoutes
              },
              {
                path: 'monitoring-moderation',
                auth: { scopes: MONITORING_MODERATION_APPLET_SCOPES, mode: 'all' },
                element: <Outlet />,
                children: monitoringModerationRoutes
              },
              {
                path: 'location-management',
                auth: { scopes: ALL_LOCATION_MANAGEMENT_SCOPES, mode: 'any' },
                element: <Outlet />,
                children: locationManagementRoutes
              }
            ]
          }
        ]
      },
      {
        path: 'unauthorized',
        element: <UnauthorizedPage />
      },
      {
        path: 'not-found',
        element: <NotFoundPage />
      },
      {
        path: '*',
        element: <NotFoundPage />
      }
    ]
  }
]

export const routes = toRouteObjects(appRoutes)
registerRoutes(routes)
export const appRouter = createBrowserRouter(routes)
